import React from "react";
import { DotLegalCheckbox, DotLegalDialog, DotLegalHeader, DotLegalMultiSelect, DotLegalSelect, DotLegalTextField } from "@dotlegal/dotlegal-ui-components";
import RecurrenceSection from "../../../annualWheel/components/RecurrenceSection.tsx";
import { useCustomTemplateAddEditDialog } from "./CustomTemplateAddEditDialog.hooks.ts";
import { Box, Collapse } from "@mui/material";
import { AnnualWheelTemplateModel } from "./CustomTemplateAddEditDialog.types.ts";
import { useAnnualWheelDialogStyles } from "../../../annualWheel/annualWheelDialog/AnnualWheelDialog.styles.ts";
import { usePlanContext } from "../../../auth/planProvider/PlanProvider.tsx";
import { useTranslation } from "../../../localization/useTranslation.ts";
import PlanInputFieldWrapper from "../../../plan/planInputFieldWrapper/PlanInputFieldWrapper.tsx";
import AnnualWheelSubTasks from "../../../annualWheel/AnnualWheelSubTasks.tsx";
import { isTemplateCustomer } from "../../../common/templateCustomerHelper.ts";
import { PurpleLockIcon } from "../../../common/icons.tsx";
import { useNotificationPeriodInDaysErrorText, useRecurringIntervalTypeSelectOptions } from "../../../annualWheel/AnnualWheel.hooks.ts";
import DotLegalSection from "../../../common/components/dotLegalSection/DotLegalSection.tsx";
import TemplateRelativeDateConfig from "../../../common/components/relativeDateConfig/TemplateRelativeDateConfig.tsx";
import MentionInput from "@common/components/mentionInput/MentionInput.tsx";
import TagsMultiSelect from "@common/components/tagsMultiSelect/TagsMultiSelect";
import { TagRelationType } from "@common/components/tagsMultiSelect/TagsMultiSelect.types";
import { usePermissionsProvider } from "../../../auth/usePermissionsProvider.ts";

export interface ICustomTemplateAddEditDialogProps {
    templateModel?: AnnualWheelTemplateModel;
    onCancel: () => void;
    onCreated: (customTemplateModel: AnnualWheelTemplateModel) => void;
    onUpdated: () => void;
    isObligation?: boolean;
}

function CustomTemplateAddEditDialog(props: ICustomTemplateAddEditDialogProps) {
    const styles = useAnnualWheelDialogStyles();
    let notificationPeriodInDaysErrorText = useNotificationPeriodInDaysErrorText();
    const recurringIntervalTypeOptions = useRecurringIntervalTypeSelectOptions();
    const { platformFeatures } = usePlanContext();
    const { hasTagsAccess } = usePermissionsProvider();
    const { translateString } = useTranslation();
    const {
        onSave,
        templateModel,
        modelUpdater,
        showError,
        isEdit,
        complianceAreasLoading,
        selectableComplianceAreas,
        annualWheelActivityGeneralTypes,
        isSaving,
        selectableEntityTemplates,
        entityTemplatesLoading,
        selectableBusinessAreas,
        businessAreasLoading,
    } = useCustomTemplateAddEditDialog(props);

    function getDialogHeader() {
        if (isEdit) {
            return translateString("editActivity");
        } else {
            return translateString("createActivity");
        }
    }

    const getHeader = () => {
        return (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <Box>{getDialogHeader()}</Box>
                <DotLegalHeader headerStyle="extraSmall">
                    {props.isObligation ? translateString("obligation") : translateString("annualWheelActivity")}
                </DotLegalHeader>
            </Box>
        );
    };

    return (
        <DotLegalDialog
            header={getHeader()}
            buttonOkText={isEdit ? translateString("save") : translateString("create")}
            open
            size={"md"}
            onDialogClose={props.onCancel}
            onOkClick={onSave}
            secondaryButtonText={translateString("cancel")}
            onSecondaryButtonClick={props.onCancel}
            buttonRight
            inProgress={isSaving}
        >
            <Box sx={styles.contentWrapper}>
                <Box sx={styles.formSpacing}>
                    <DotLegalHeader headerStyle="small" marginTop={2} marginBottom={0.5}>
                        {translateString("createActivityName")}
                    </DotLegalHeader>
                    <DotLegalTextField
                        noMargin
                        debounce={false}
                        label=""
                        value={templateModel.name}
                        onChange={modelUpdater.onNameChange}
                        errorText={showError && templateModel.isNameInvalid() ? translateString("fieldRequired") : undefined}
                    />

                    {!props.isObligation && (
                        <>
                            <DotLegalHeader headerStyle={"small"} marginTop={4} marginBottom={0.5}>
                                {translateString("addAnnualWheelActivityGeneralType")}
                            </DotLegalHeader>
                            <DotLegalSelect
                                selectedItem={templateModel.generalType?.toString() ?? ""}
                                options={annualWheelActivityGeneralTypes(!props.isObligation)}
                                onChange={(generalType) => modelUpdater.onGeneralTypeChange(generalType)}
                                placeholder={""}
                                label={""}
                                noMargin
                                noOptionsLabel={translateString("noOptions")}
                            />
                        </>
                    )}
                    <DotLegalHeader headerStyle="small" marginTop={4} marginBottom={0.5}>
                        {translateString("createActivityDescription")}
                    </DotLegalHeader>
                    <MentionInput
                        noMargin
                        multiline
                        rows={5}
                        debounce
                        label=""
                        value={templateModel.description}
                        onChange={modelUpdater.onDescriptionChange}
                        disableMentions
                    />

                    {props.isObligation && (
                        <>
                            <DotLegalHeader headerStyle={"small"} marginBottom={0.5} marginTop={4}>
                                {translateString("annualWheelStartDate")}
                            </DotLegalHeader>

                            <DotLegalSection>
                                <DotLegalHeader headerStyle={"extraSmall"} marginBottom={0.5}>
                                    {translateString("relativeStartDate")}
                                </DotLegalHeader>
                                <TemplateRelativeDateConfig
                                    config={templateModel.startDateRelativeConfig}
                                    onValueChange={modelUpdater.onStartDateRelativeValueChange}
                                    onUnitChange={modelUpdater.onStartDateRelativeUnitChange}
                                    onDirectionChange={modelUpdater.onStartDateRelativeDirectionChange}
                                    onRelativeToChange={modelUpdater.onStartDateRelativeToChange}
                                    errorMsg={showError && templateModel.isStartDateInvalid() ? translateString("templateRelativeStartDateError") : undefined}
                                />

                                <Collapse in={!!templateModel.recurringInterval}>
                                    <DotLegalHeader headerStyle={"extraSmall"} marginTop={1} marginBottom={0.5}>
                                        {translateString("relativeEndDate")}
                                    </DotLegalHeader>
                                    <TemplateRelativeDateConfig
                                        config={templateModel.endDateRelativeConfig}
                                        errorMsg={showError && templateModel.isEndDateInvalid() ? translateString("templateRelativeEndDateError") : undefined}
                                        onValueChange={modelUpdater.onEndDateRelativeValueChange}
                                        onUnitChange={modelUpdater.onEndDateRelativeUnitChange}
                                        onDirectionChange={modelUpdater.onEndDateRelativeDirectionChange}
                                        onRelativeToChange={modelUpdater.onEndDateRelativeToChange}
                                    />
                                </Collapse>
                            </DotLegalSection>
                        </>
                    )}

                    <RecurrenceSection
                        interval={templateModel.recurringInterval}
                        intervalType={templateModel.recurringIntervalType}
                        intervalTypeOptions={recurringIntervalTypeOptions}
                        onIntervalChange={modelUpdater.onIntervalChange}
                        onIntervalTypeChange={(t) => modelUpdater.onRecurringIntervalTypeChange(t)}
                        onSingularChange={modelUpdater.onSingularActivityChange}
                        showError={showError}
                        isIntervalInvalid={templateModel.isRecurringIntervalInvalid()}
                    />

                    <DotLegalHeader headerStyle={"small"} marginTop={4} marginBottom={0.5}>
                        {translateString("annualWheelNotifications")}
                    </DotLegalHeader>
                    <Box sx={styles.content}>
                        <Box sx={styles.notificationPeriod}>
                            <Box sx={styles.numberField}>
                                <DotLegalTextField
                                    onlyNumber={"integer"}
                                    label={""}
                                    value={templateModel.notificationPeriodInDays?.toString() ?? null}
                                    debounce={false}
                                    onChange={modelUpdater.onNotificationPeriodInDaysChange}
                                    noMargin
                                />
                            </Box>
                            <span>{translateString("days").toLowerCase()}</span>
                        </Box>
                    </Box>
                    {showError && templateModel.isNotificationPeriodInvalid() && (
                        <Box sx={styles.error}>{notificationPeriodInDaysErrorText(templateModel.notificationPeriodInDays)}</Box>
                    )}

                    <DotLegalHeader headerStyle={"small"} marginTop={4} marginBottom={0.5} toolTip={translateString("customIdToolTip")}>
                        {translateString("annualWheelCustomIdHeader")}
                    </DotLegalHeader>
                    <DotLegalTextField label={""} value={templateModel.customId ?? null} debounce={false} noMargin onChange={modelUpdater.onCustomIdChange} />

                    <DotLegalHeader headerStyle={"small"} marginTop={4} marginBottom={0.5} toolTip={translateString("complianceAreaToolTip")}>
                        {translateString("areasForAnnualWheelTask")}
                    </DotLegalHeader>

                    <PlanInputFieldWrapper hasAccess={platformFeatures.annualWheelComplianceArea} hoverText={translateString("upgradePrivacyPlanToday")}>
                        <DotLegalMultiSelect
                            label={""}
                            options={selectableComplianceAreas}
                            isLoading={complianceAreasLoading}
                            chosenOptions={templateModel.complianceAreaIds}
                            placeholder={""}
                            onChange={modelUpdater.onComplianceAreasChange}
                            noMargin
                            noOptionsLabel={translateString("noOptions")}
                            disabled={!platformFeatures.annualWheelComplianceArea}
                            icon={!platformFeatures.annualWheelComplianceArea ? PurpleLockIcon : undefined}
                        />
                    </PlanInputFieldWrapper>

                    {props.isObligation && (
                        <>
                            <DotLegalHeader headerStyle={"small"} marginTop={4} marginBottom={0.5}>
                                {translateString("businessAreasForTask")}
                            </DotLegalHeader>
                            <DotLegalMultiSelect
                                label={""}
                                options={selectableBusinessAreas}
                                isLoading={businessAreasLoading}
                                chosenOptions={templateModel.businessAreaIds}
                                placeholder={""}
                                onChange={modelUpdater.onBusinessAreasChange}
                                noMargin
                                noOptionsLabel={translateString("noOptions")}
                            />

                            {hasTagsAccess && (
                                <>
                                    <DotLegalHeader headerStyle={"small"} marginTop={4} marginBottom={0.5}>
                                        {translateString("addTagsToAnnualWheelActivity")}
                                    </DotLegalHeader>
                                    <TagsMultiSelect
                                        isLoading={false}
                                        hideLabel
                                        noMargin
                                        entityType={TagRelationType.AnnualWheelActivityTemplate}
                                        selectedTags={templateModel.tagIds}
                                        onChange={modelUpdater.onTagsChange}
                                    />
                                </>
                            )}

                            <DotLegalHeader
                                headerStyle={"small"}
                                marginTop={4}
                                marginBottom={0.5}
                                toolTip={translateString("annualWheelTemplateContractTemplateInfo")}
                            >
                                {translateString("linkObligationsToContractTemplate")}
                            </DotLegalHeader>
                            <DotLegalSection>
                                <DotLegalMultiSelect
                                    label={""}
                                    options={selectableEntityTemplates}
                                    isLoading={entityTemplatesLoading}
                                    chosenOptions={templateModel.entityTemplates.map((ct) => ct.entityTemplateId)}
                                    placeholder={""}
                                    onChange={(ids) => modelUpdater.onEntityTemplatesChange(ids)}
                                    noMargin
                                    noOptionsLabel={translateString("noOptions")}
                                />

                                {templateModel.entityTemplates.length > 0 && (
                                    <Box>
                                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                                            <DotLegalHeader center headerStyle={"extraSmall"} marginTop={3} marginBottom={1}>
                                                {translateString("annualWheelTemplateContractTemplateMandatory")}
                                            </DotLegalHeader>
                                        </Box>
                                        {templateModel.entityTemplates.map((entityTemplate) => (
                                            <Box key={entityTemplate.entityTemplateId} sx={{ display: "flex", alignItems: "center" }}>
                                                <Box sx={{ width: 300 }}>
                                                    <DotLegalHeader marginBottom={0} headerStyle="extraSmall" fontWeight={400}>
                                                        {entityTemplate.entityTemplateName}
                                                    </DotLegalHeader>
                                                </Box>
                                                <DotLegalCheckbox
                                                    checked={entityTemplate.isMandatory}
                                                    onChange={(checked) =>
                                                        modelUpdater.onEntityTemplateMandatoryChange(entityTemplate.entityTemplateId, checked)
                                                    }
                                                    label={translateString("required")}
                                                    fontSize="s"
                                                />
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </DotLegalSection>
                        </>
                    )}

                    <DotLegalHeader marginBottom={0.5} marginTop={3} headerStyle="small">
                        {translateString("createActivitySubTasks")}
                    </DotLegalHeader>
                    <DotLegalSection>
                        <AnnualWheelSubTasks
                            subTasks={templateModel.subTasks.map((x) => ({ name: x.name, relations: [] }))}
                            onChange={modelUpdater.onSubTaskChange}
                        />
                    </DotLegalSection>

                    {!isEdit && !isTemplateCustomer() && !props.isObligation && (
                        <>
                            <DotLegalHeader
                                marginBottom={0.5}
                                marginTop={4}
                                headerStyle="small"
                                toolTip={translateString("planAnnualWheelActivityImmediatelyDesc")}
                            >
                                {translateString("planAnnualWheelActivityImmediately")}
                            </DotLegalHeader>

                            <Box sx={(theme) => ({ display: "flex", alignItems: "center", marginLeft: "-11px" })}>
                                <DotLegalCheckbox
                                    onChange={modelUpdater.onPlanNowChange}
                                    label={translateString("planNow")}
                                    checked={templateModel.planNow}
                                ></DotLegalCheckbox>
                            </Box>
                        </>
                    )}
                </Box>
            </Box>
        </DotLegalDialog>
    );
}

export default CustomTemplateAddEditDialog;
